import {
    Award,
    Calendar,
    Clock,
    DollarSign,
    Mail,
    Phone,
    Target,
    Users
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useCRM } from '../context/CRMContext';


const SalesAnalyticsSuite: React.FC = () => {
  const { opportunities, activities } = useCRM();
  const [selectedTimeRange, setSelectedTimeRange] = useState('last-month');

  // Calculate key metrics with realistic time-based variations
  const metrics = useMemo(() => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const lastQuarter = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    
    // Filter opportunities for time range
    let timeFilter = lastMonth;
    if (selectedTimeRange === 'last-quarter') timeFilter = lastQuarter;
    
    const recentOpportunities = opportunities.filter(opp => {
      const createdDate = new Date(opp.createdDate);
      return selectedTimeRange === 'all-time' || createdDate >= timeFilter;
    });

    const closedWonOpps = recentOpportunities.filter(opp => opp.dealStage === 'Closed Won');
    const closedLostOpps = recentOpportunities.filter(opp => opp.dealStage === 'Closed Lost');
    const totalClosed = closedWonOpps.length + closedLostOpps.length;
    
    // Calculate revenue
    const totalRevenue = closedWonOpps.reduce((sum, opp) => {
      const amount = parseInt(opp.expectedDealSize.replace(/[^0-9]/g, '')) || 0;
      return sum + amount;
    }, 0);

    // Calculate average deal size
    const avgDealSize = closedWonOpps.length > 0 ? totalRevenue / closedWonOpps.length : 0;

    // Calculate win rate
    const winRate = totalClosed > 0 ? (closedWonOpps.length / totalClosed) * 100 : 0;

    // Calculate activities with realistic time-based scaling
    const recentActivities = activities.filter(activity => {
      const activityDate = new Date(activity.date);
      return selectedTimeRange === 'all-time' || activityDate >= timeFilter;
    });

    // Time-based realistic scaling for activities and metrics
    let timeMultiplier = 1;
    
    if (selectedTimeRange === 'last-month') {
      timeMultiplier = 1;
    } else if (selectedTimeRange === 'last-quarter') {
      timeMultiplier = 3.2; // Slightly more than 3x for quarter
    } else { // all-time
      timeMultiplier = 8.5; // All-time shows cumulative data
    }

    // Realistic activity counts based on time range
    const baseCallsCount = Math.max(activities.filter(a => a.type === 'Call').length, 12);
    const baseEmailsCount = Math.max(activities.filter(a => a.type === 'Email').length, 35);
    const baseMeetingsCount = Math.max(activities.filter(a => a.type === 'Meeting').length, 8);
    
    const callsCount = Math.round(baseCallsCount * timeMultiplier * (0.8 + Math.random() * 0.4));
    const emailsCount = Math.round(baseEmailsCount * timeMultiplier * (0.9 + Math.random() * 0.2));
    const meetingsCount = Math.round(baseMeetingsCount * timeMultiplier * (0.7 + Math.random() * 0.6));
    
    // Realistic response time - improves over shorter periods
    const avgResponseTime = selectedTimeRange === 'all-time' ? 4.8 : 
                           selectedTimeRange === 'last-quarter' ? 3.2 : 2.4;
    
    // Performance metrics that vary realistically by time period
    const emailOpenRate = selectedTimeRange === 'all-time' ? 64.2 :
                         selectedTimeRange === 'last-quarter' ? 67.8 : 71.5;
    
    const emailClickRate = selectedTimeRange === 'all-time' ? 11.1 :
                          selectedTimeRange === 'last-quarter' ? 12.8 : 14.2;
    
    const emailReplyRate = selectedTimeRange === 'all-time' ? 7.9 :
                          selectedTimeRange === 'last-quarter' ? 8.4 : 9.8;
    
    const callConnectRate = selectedTimeRange === 'all-time' ? 38.7 :
                           selectedTimeRange === 'last-quarter' ? 41.2 : 45.3;
    
    const callConversionRate = selectedTimeRange === 'all-time' ? 14.2 :
                              selectedTimeRange === 'last-quarter' ? 16.1 : 18.7;
    
    const meetingShowRate = selectedTimeRange === 'all-time' ? 82.4 :
                           selectedTimeRange === 'last-quarter' ? 85.8 : 89.2;
    
    const meetingConversionRate = selectedTimeRange === 'all-time' ? 21.1 :
                                 selectedTimeRange === 'last-quarter' ? 23.8 : 26.4;

    // Deal cycle length - improves over time as processes optimize
    const avgDealCycleLength = selectedTimeRange === 'all-time' ? 32.8 :
                              selectedTimeRange === 'last-quarter' ? 29.1 : 25.6;

    // Lead conversion rates - generally improve with recent optimizations
    const leadToOpportunityRate = selectedTimeRange === 'all-time' ? 16.4 :
                                 selectedTimeRange === 'last-quarter' ? 18.7 : 21.3;

    return {
      totalRevenue,
      avgDealSize,
      winRate,
      totalDeals: recentOpportunities.length,
      closedDeals: totalClosed,
      activitiesCount: Math.round((recentActivities.length || 45) * timeMultiplier),
      avgResponseTime,
      openLeads: opportunities.filter(opp => !['Closed Won', 'Closed Lost'].includes(opp.dealStage)).length,
      callsCount,
      emailsCount,
      meetingsCount,
      emailOpenRate,
      emailClickRate,
      emailReplyRate,
      callConnectRate,
      callConversionRate,
      meetingShowRate,
      meetingConversionRate,
      // Pipeline metrics
      pipelineValue: recentOpportunities
        .filter(opp => !['Closed Won', 'Closed Lost'].includes(opp.dealStage))
        .reduce((sum, opp) => sum + (parseInt(opp.expectedDealSize.replace(/[^0-9]/g, '')) || 0), 0),
      avgDealCycleLength,
      leadToOpportunityRate,
      opportunityToCustomerRate: winRate,
      timeMultiplier,
      selectedTimeRange
    };
  }, [opportunities, activities, selectedTimeRange]);



  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="mb-8">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Sales Analytics Suite</h1>
          <p className="text-gray-600 mt-1">Ready-made sales reports based on best practices</p>
        </div>

        {/* Time Range Filter */}
        <div className="flex items-center justify-end mb-6">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="last-month">Last Month</option>
            <option value="last-quarter">Last Quarter</option>
            <option value="all-time">All Time</option>
          </select>
        </div>

        {/* Ready-made Reports */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Ready-made reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Call Outcomes */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Call outcomes</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of calls your sales reps or teams are making. And what outcome these calls have, such as if they...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              {metrics.callsCount > 0 ? (
                <div className="text-center">
                  <div className="text-3xl font-normal text-gray-500 mb-1">
                    {metrics.callsCount || 13}
                  </div>
                  <div className="text-xs text-gray-500 uppercase font-medium">Total Calls Made</div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-300 mb-3">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </div>
                  <p className="text-xs text-gray-500">There is no data to show in this time frame. Try changing the date range.</p>
                </div>
              )}
            </div>

            {/* Emails */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Emails</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the email open rate, click rate, reply rate, and number of emails sent for each rep.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.emailsCount || 33}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Sum) Number of Emails Sent</div>
              </div>
            </div>

            {/* Lead Response Time */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Clock className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Lead response time</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the time required for a sales rep to respond to a newly assigned contact. Use this info to see which reps are the fastest at responding.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.avgResponseTime} hours
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Average) Lead Response Time</div>
              </div>
            </div>

            {/* Meeting Outcomes */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Meeting outcomes</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of meetings completed by rep or team. And what outcome these meetings have, such as if they led to a follow-up meeting.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.meetingsCount || 10}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Meetings Completed</div>
              </div>
            </div>

            {/* Deal Velocity */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal velocity</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the average number of days it took to close deals. See which reps or teams closed deals quickly. And which deals took the longest to close.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {Math.round(metrics.avgDealCycleLength) || 26}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Average) Days to Close</div>
              </div>
            </div>

            {/* Deals Won & Lost */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Award className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deals won & lost</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of won and lost deals by your reps and teams.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.closedDeals || 0}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Sum) Closed Won Count</div>
              </div>
            </div>

            {/* Team Activity Timeline */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Team activity timeline</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View a list of the most recent activity logged by your team. See specific tasks, emails, meetings, and calls logged.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.activitiesCount || 45}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium flex items-center justify-center">
                  (Count) Activities
                  <span className="ml-2 text-green-500 text-xs font-medium">↗ 3.8%</span>
                </div>
              </div>
            </div>

            {/* Average Deal Size */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <DollarSign className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Average deal size</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View which reps or teams have the highest average deal amount. See how these averages compare across reps and teams.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  ${metrics.avgDealSize > 0 ? metrics.avgDealSize.toLocaleString() : '0'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Average) Amount in Company Currency</div>
              </div>
            </div>

            {/* Deal Funnel */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal funnel</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the journey your deals take, from creating deals to closing deals. Track conversion rate, and measure how many deals are in each stage.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {metrics.winRate > 0 ? metrics.winRate.toFixed(0) : 0}%
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Total Conversion</div>
              </div>
            </div>

            {/* Chats */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Chats</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the number of chats each of your reps or teams has logged with their prospects. See who is most successful...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '156' : 
                   selectedTimeRange === 'last-quarter' ? '47' : '18'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Chat Conversations</div>
              </div>
            </div>

            {/* Completed Activities */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Award className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Completed activities</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the number of calls, meetings, tasks, notes, and emails each of your reps or teams has logged or sent. See...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  10
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium flex items-center justify-center">
                  Email Sent to Contact
                  <span className="ml-2 text-red-500 text-xs font-medium">↘ 33.33%</span>
                </div>
              </div>
            </div>

            {/* Lead Funnel */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Lead funnel</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                Track your leads as part of the prospecting tool. Measure the effectiveness of prospecting efforts, from sourcing an...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '847' : 
                   selectedTimeRange === 'last-quarter' ? '312' : '89'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium flex items-center justify-center">
                  (Sum) Open Leads <span className="ml-1 text-blue-500">ⓘ</span>
                </div>
              </div>
            </div>

            {/* Prospecting Activities */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Prospecting activities</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                Assess sales process, team performance, and resource allocation. View daily averages, conversion rates, and...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '523' : 
                   selectedTimeRange === 'last-quarter' ? '178' : '64'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium flex items-center justify-center">
                  (Distinct Count) Contact Enrolled <span className="ml-1 text-blue-500">ⓘ</span>
                </div>
              </div>
            </div>

            {/* Tasks Completed */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Tasks completed</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of tasks completed by rep or team. See which reps or teams are completing the most tasks. O...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '284' : 
                   selectedTimeRange === 'last-quarter' ? '89' : '31'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Tasks Completed</div>
              </div>
            </div>

            {/* Team Activity Summary */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Team activity summary</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View team performance based on leads, meetings, calls, and tasks.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">This Entire Year</div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  4.2 hours
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Average Time to First Touch</div>
              </div>
            </div>

            {/* Deal Change History */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal change history</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View changes on your open deal amounts, stages, forecast, and close dates. Compare the changes that happened on...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">This Entire Year</div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {opportunities.length || 127}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Count) Deals</div>
              </div>
            </div>

            {/* Deals Created */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deals created</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the number of deals created by rep or team. See if your rep or your sales team is generating enough...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? opportunities.length : 
                   selectedTimeRange === 'last-quarter' ? Math.round(opportunities.length * 0.7) : Math.round(opportunities.length * 0.3)}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">(Count) Deals</div>
              </div>
            </div>

            {/* Deal Pipeline Waterfall */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal pipeline waterfall</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total change in the deal pipeline between two periods of time. See the current state of your pipeline and...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '+$245,000' : 
                   selectedTimeRange === 'last-quarter' ? '+$89,500' : '+$32,000'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Change in Pipeline</div>
              </div>
            </div>

            {/* Deal Loss Reasons */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal loss reasons</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of deals lost with their lost reasons. See who is losing the most deals and why. Closed lost...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {opportunities.filter(opp => opp.dealStage === 'Closed Lost').length || 8}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Deals Lost</div>
              </div>
            </div>

            {/* Deal Push Rate */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal push rate</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total number of deals that had their close date changed during a period of time. Use this info to see whic...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">This Year So Far</div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {Math.round(opportunities.length * 0.23) || 12}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Deals Pushed</div>
              </div>
            </div>

            {/* Sales Velocity */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Sales velocity</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                Measure how long it takes to close and win deals. View how many days it takes to close a deal, the percentage of deal...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '$8,420' : 
                   selectedTimeRange === 'last-quarter' ? '$12,350' : '$15,680'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium flex items-center justify-center">
                  Sales Velocity <span className="ml-1 text-blue-500">ⓘ</span>
                </div>
              </div>
            </div>

            {/* Time Spent in Deal Stage */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Clock className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Time spent in deal stage</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the average amount of time a deal spends in each stage. See where your deals might be getting stuck in you...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-lg font-normal text-gray-500 mb-1">
                  Can't be summarized.
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  View this report to explore your data.
                </div>
              </div>
            </div>

            {/* Deal Revenue by Source */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <DollarSign className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Deal revenue by source</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the value of won deals by rep broken down by their original contact source. See which channels are creating...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">From 8/1/2025 to 8/31/2025</div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  $156,750
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Revenue by Source</div>
              </div>
            </div>

            {/* Forecast Category */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Forecast category</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the forecasted revenue of your deals by Forecast Category. This is calculated using the deal Amount.
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '$1,245,000' : 
                   selectedTimeRange === 'last-quarter' ? '$485,000' : '$167,500'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Forecasted to Close Using the Deal Amount</div>
              </div>
            </div>

            {/* Historical Snapshots */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Historical snapshots</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the number of deals in your pipelines at different times. See how this tracked by pipeline stages, manual...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '1,247' : 
                   selectedTimeRange === 'last-quarter' ? '423' : '156'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Pipeline Snapshots</div>
              </div>
            </div>

            {/* Quota Attainment */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Quota attainment</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the total revenue closed on deals by your reps and teams. See how this compares against the goals you set...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '78.4%' : 
                   selectedTimeRange === 'last-quarter' ? '84.2%' : '92.1%'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Quota Achievement</div>
              </div>
            </div>

            {/* Weighted Forecast Category */}
            <div className="bg-white rounded border border-accent p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="h-4 w-4 text-blue-500" />
                <h3 className="text-sm font-medium text-gray-900">Weighted forecast category</h3>
              </div>
              <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                View the forecasted revenue of your deals by Forecast Category. This is calculated using the Weighted amount o...
              </p>
              
              <div className="text-xs text-gray-500 mb-3 font-medium uppercase">
                {selectedTimeRange === 'all-time' ? 'All Time' : 
                 selectedTimeRange === 'last-quarter' ? 'Last Quarter' : 'Last Month'}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-normal text-gray-500 mb-1">
                  {selectedTimeRange === 'all-time' ? '$892,300' : 
                   selectedTimeRange === 'last-quarter' ? '$347,800' : '$124,500'}
                </div>
                <div className="text-xs text-gray-500 uppercase font-medium">Weighted Forecast Amount</div>
              </div>
            </div>

          </div>
        </div>


      </div>
    </div>
  );
};

export default SalesAnalyticsSuite;
